import type { ServerPaginationConfig } from '@shared/types/module-config';

export const WITHDRAWALS_PAGINATION: ServerPaginationConfig = {
  enabled: true,
  mode: 'server',
  pageParam: 'page',
  sizeParam: 'size',
  defaultSize: 25,
  firstPage: 1,
  sendPageIfDefault: true,
};

export const ATTEMPTS_PAGINATION: ServerPaginationConfig = {
  enabled: true,
  mode: 'server',
  pageParam: 'page',
  sizeParam: 'size',
  defaultSize: 25,
  firstPage: 0,
  sendPageIfDefault: true,
};

export const WITHDRAWALS_ROUTE_PATHS = {
  withdrawals: '/movements/withdrawals',
};

export const WITHDRAWALS_API_PATHS = {
  withdrawals: '/v1/api/platform/withdrawals',
  withdrawalDetail: '/v1/api/platform/withdrawals',
  changeStatus: '/v1/api/platform/withdrawals/movement/status',
  changeProvider: '/v1/api/platform/withdrawals/movement/provider',
  reprocess: '/v1/api/platform/withdrawals/reprocess',
  reject: '/v1/api/platform/withdrawals/reject',
  attempts: '/v1/api/platform/withdrawals/attemps',
  paymentMethods: '/v1/api/platform/withdrawals/payment-methods',
  statusFilter: '/v1/api/platform/withdrawals/status-filter',
  wmCombo: '/v1/api/platform/withdrawals/wm-combo',
  massiveChangeStatus: '/v1/api/platform/withdrawals/status',
  // Bulk payout status change (PAY-5202). Two inputs, one operation:
  // `selection` takes payout ids and resolves each real current_status server-side;
  // `batchList` takes a CSV that carries current_status per row.
  changePayoutStatusSelection: '/v1/api/platform/withdrawals/change-status/selection',
  changePayoutStatusBatch: '/v1/api/platform/withdrawals/change-status/batch-list',
};

/**
 * Columns the payout batch CSV must contain. Order is irrelevant — the backend builds a
 * header→index map from row 0 — but all six must be present or it rejects the file.
 */
export const PAYOUT_BATCH_CSV_HEADERS = [
  'payout_id',
  'gateway_id',
  'current_status',
  'new_status',
  'gateway_reference',
  'status_reason_code',
] as const;

/**
 * Statuses a stuck payout can be moved to.
 *
 * processor-gateway allows `UNKNOWN → REJECTED | PENDING | COMPLETED`. Only REJECTED and
 * COMPLETED are offered: moving a payout back to PENDING from the panel does not resolve
 * anything, it just re-queues a payout that is already stuck.
 */
export const PAYOUT_NEW_STATUSES = ['REJECTED', 'COMPLETED'] as const;

/**
 * The one status a `status_reason_code` is required for.
 *
 * Named rather than inlined because the rule is not cosmetic: on a rejection the code decides
 * whether withdrawals retries the payout through another gateway or returns the balance to the
 * user, so an absent or off-catalogue value silently changes the outcome. On a `COMPLETED` the
 * field records nothing that happened and every catalogue value would be a lie.
 */
export const PAYOUT_STATUS_REJECTED = 'REJECTED';

/**
 * The closed list of `status_reason_code` values.
 *
 * A closed list because free text has already corrupted production: 108 rows across 7
 * gateways in three months, including `GENRIC ERROR` (49 rows), a bare `Rejected` (28) and
 * an md5 hash (23). That is systemic, not one operator's typo, and it makes a batch
 * impossible to audit after the fact.
 *
 * Every entry names a *failure*, which is why the list is only enforced as mandatory on
 * `REJECTED` — see `PAYOUT_STATUS_REJECTED`.
 *
 * Debt: the canonical catalogue lives in `commons`' `ErrorCodeConstants` and no endpoint
 * exposes it, so this list is a frontend constant that can drift. Replace it with a fetch
 * when an endpoint exists.
 */
export const PAYOUT_STATUS_REASON_CODES = [
  'GENERIC_ERROR',
  'PROVIDER_WITHOUT_BALANCE',
  'INVALID_BANK_ACCOUNT',
  'NOT_AUTHORIZED',
  'INVALID_REQUEST',
  'PROVIDER_FRAUD_RISK',
  'INVALID_ALIAS',
  'REJECTED_BY_BANK',
  'DNI_MISMATCH',
  'TEMPORARY_BANK_ISSUES',
  'TEMPORARY_PROVIDER_ISSUES',
  'PROVIDER_NO_INFORMATION',
  'BENEFICIARY_MISMATCH',
  'INCORRECT_KEY_FORMAT',
  'INVALID_DOCUMENT',
  'EXCEEDED_USER_LIMIT',
  'EXCEEDED_MONTHLY_AMOUNT',
  'ACCOUNT_TYPE_NOT_SUPPORTED',
  'INSUFFICIENT_FUNDS',
  'PROVIDER_INTERNAL_ERROR',
] as const;

/**
 * Maximum payouts per selection submission. Mirrors `PayoutStatusSelectionRequestDto.MAX_PAYOUTS`
 * — the backend rejects more, because it makes one upstream status lookup per row. Enforced here
 * too so the operator is told before submitting rather than after waiting.
 */
export const PAYOUT_SELECTION_MAX = 100;
